import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QrCode, Download, FileDown, Loader2 } from "lucide-react";
import { jsPDF } from "jspdf";
import { renderBrandedQr, renderQrPoster, downloadCanvas } from "@/lib/qr";

const SITE = "https://fysikiepilogi.gr";
const PRESETS = [
  ["Αρχική σελίδα", `${SITE}/`, "Σκανάρετε για το site μας"],
  ["Περιοχή Μελών", `${SITE}/members`, "Σκανάρετε για την Περιοχή Μελών"],
  ["Εγγραφές / Επικοινωνία", `${SITE}/#enroll`, "Σκανάρετε για εγγραφή"],
  ["Ραντεβού Επαγγ. Προσανατολισμού", `${SITE}/#career`, "Σκανάρετε για ραντεβού"],
  ["Βάσεις 2026 & Μόρια", `${SITE}/vaseis`, "Σκανάρετε για τις Βάσεις 2026"],
  ["Διαγνωστικά Τεστ", `${SITE}/quiz`, "Σκανάρετε για δωρεάν τεστ"],
];
const inp = "w-full h-10 px-3 border border-border rounded-sm bg-white text-sm focus:outline-none focus:border-primary";
const btn = "inline-flex items-center justify-center gap-2 h-10 px-4 rounded-sm font-mono-accent text-[10px] uppercase tracking-widest disabled:opacity-60";
const slug = (s) => s.replace(/https?:\/\//, "").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_|_$/g, "") || "qr";

export default function AdminQr() {
  const [url, setUrl] = useState(PRESETS[1][1]);
  const [caption, setCaption] = useState(PRESETS[1][2]);
  const [busy, setBusy] = useState("");
  const previewRef = useRef(null);

  useEffect(() => {
    if (!url.trim()) return;
    const t = setTimeout(async () => {
      try {
        const c = await renderBrandedQr({ url: url.trim(), size: 512 });
        const el = previewRef.current; if (!el) return;
        el.width = c.width; el.height = c.height; el.getContext("2d").drawImage(c, 0, 0);
      } catch (e) { /* invalid url */ }
    }, 250);
    return () => clearTimeout(t);
  }, [url]);

  const pngQr = async () => { setBusy("png"); try { downloadCanvas(await renderBrandedQr({ url: url.trim(), size: 2048 }), `QR_${slug(url)}.png`); } catch (e) { toast.error("Αποτυχία δημιουργίας QR."); } finally { setBusy(""); } };
  const pngPoster = async () => { setBusy("poster"); try { downloadCanvas(await renderQrPoster({ url: url.trim(), caption }), `QR_Αφίσα_${slug(url)}.png`); } catch (e) { toast.error("Αποτυχία δημιουργίας αφίσας."); } finally { setBusy(""); } };
  const pdfPoster = async () => {
    setBusy("pdf");
    try {
      const c = await renderQrPoster({ url: url.trim(), caption, size: 2048 });
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const w = 170, h = (c.height / c.width) * w;
      doc.addImage(c.toDataURL("image/jpeg", 0.92), "JPEG", (210 - w) / 2, (297 - h) / 2, w, h);
      doc.save(`QR_Αφίσα_${slug(url)}.pdf`);
    } catch (e) { toast.error("Αποτυχία δημιουργίας PDF."); } finally { setBusy(""); }
  };

  return (
    <section className="lg:col-span-5" data-testid="admin-qr">
      <div className="font-mono-accent text-[10px] uppercase tracking-widest text-accent mb-2">// QR Codes</div>
      <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-4"><QrCode className="h-5 w-5" /> Δημιουργία QR Code</h2>
      <div className="bg-white border border-border rounded-sm p-5 grid md:grid-cols-5 gap-6">
        <div className="md:col-span-3 space-y-4">
          <div>
            <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Γρήγορη επιλογή</div>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map(([l, u, cap]) => <button key={u} type="button" data-testid={`qr-preset-${slug(u)}`} onClick={() => { setUrl(u); setCaption(cap); }} className={`h-8 px-3 rounded-sm border text-xs transition-colors ${url === u ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{l}</button>)}
            </div>
          </div>
          <label className="block"><span className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">Σύνδεσμος</span><input data-testid="qr-url" className={`${inp} mt-1`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." /></label>
          <label className="block"><span className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">Λεζάντα αφίσας</span><input data-testid="qr-caption" className={`${inp} mt-1`} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Σκανάρετε για…" maxLength={60} /></label>
          <div className="grid sm:grid-cols-3 gap-2 pt-2">
            <button data-testid="qr-download-png" disabled={!!busy} onClick={pngQr} className={`${btn} border border-primary text-primary hover:bg-primary hover:text-primary-foreground`}>{busy === "png" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} QR PNG (2048px)</button>
            <button data-testid="qr-download-poster" disabled={!!busy} onClick={pngPoster} className={`${btn} border border-primary text-primary hover:bg-primary hover:text-primary-foreground`}>{busy === "poster" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Αφίσα PNG</button>
            <button data-testid="qr-download-pdf" disabled={!!busy} onClick={pdfPoster} className={`${btn} bg-primary text-primary-foreground hover:bg-accent`}>{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Αφίσα PDF (A4)</button>
          </div>
          <p className="text-xs text-muted-foreground">Υψηλή ανάλυση για εκτύπωση (βιτρίνα, φυλλάδια, κάρτες). Το QR έχει διόρθωση σφαλμάτων «H» ώστε να σκανάρεται και με το λογότυπο στο κέντρο.</p>
        </div>
        <div className="md:col-span-2 flex flex-col items-center justify-center bg-secondary/60 rounded-sm p-4">
          <canvas ref={previewRef} data-testid="qr-preview" className="w-full max-w-[260px] h-auto rounded-sm border border-border bg-white" />
          <div className="mt-3 font-heading font-bold text-sm text-center" data-testid="qr-preview-caption">{caption || "—"}</div>
          <div className="font-mono-accent text-[10px] text-accent break-all text-center">{url.replace(/^https?:\/\//, "")}</div>
        </div>
      </div>
    </section>
  );
}
